import { Component, effect, inject, input, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

/**
 * Native-Angular port of the AngularJS `ftContestStatus` directive
 * (account-v1/.../platform/layer/photo-advocacy/contest/contestStatus.directive.js).
 * Renders one of four labels depending on the contest's lifecycle state:
 *   off / winners-announced (date) / submissions-pending / chooseWinners.
 *
 * Translation labels come through the native ngx `TranslateService.instant`
 * (the WS2 translate sweep drained the AngularJS `$translate` `$injector`
 * bridge), and the date format matches v1's `dateFormat:'MMM D, YYYY'`.
 */
@Component({
    selector: 'ft-contest-status',
    templateUrl: './contest-status.component.html',
    standalone: false
})
export class FtContestStatusComponent {
    readonly contest = input<any>();

    protected readonly statusText = signal('');

    private readonly translateSvc = inject(TranslateService);

    constructor() {
        effect(() => {
            if (this.contest()) this.statusText.set(this.getStatusText());
        });
    }

    private translate(key: string): string {
        return this.translateSvc.instant(key);
    }

    private fmt(epochOrDate: any): string {
        if (epochOrDate == null) return '';
        const d = typeof epochOrDate === 'number' ? new Date(epochOrDate) : new Date(epochOrDate);
        if (Number.isNaN(d.getTime())) return '';
        const month = d.toLocaleString('en-US', { month: 'short' });
        return `${month} ${d.getDate()}, ${d.getFullYear()}`;
    }

    private parseBool(v: any): boolean {
        if (typeof v === 'boolean') return v;
        if (v === 1 || v === '1' || v === 'true') return true;
        return false;
    }

    private isRunning(contest: any): boolean {
        const now = Date.now();
        const start = new Date(contest.startDate).getTime();
        const end = new Date(contest.votingEndDate).getTime();
        return now > start && now < end;
    }

    private getStatusText(): string {
        const off = this.translate('flipto.components.campaign.Off');
        const winnersAnnounced = this.translate('flipto.account.sections.platform.photoAdvocacy.WinnersAnnounced');
        const submissions = this.translate('flipto.account.sections.platform.photoAdvocacy.SubmissionsReviewed');
        const votingEnds = this.translate('flipto.account.sections.platform.photoAdvocacy.VotingEnds');
        const chooseWinners = this.translate('flipto.account.sections.platform.photoAdvocacy.ChooseYourWinners');

        if (!this.parseBool(this.contest().isActive)) return off;
        if (this.parseBool(this.contest().isClosed)) {
            return `${winnersAnnounced} ${this.fmt(this.contest().closeDate)}`;
        }
        if (this.isRunning(this.contest())) {
            // The contest object shape differs between submissions vs audiences contexts —
            // some have a totalUncuratedPhotos count; otherwise show the votingEnds date.
            return this.contest().totalUncuratedPhotos
                ? `${this.contest().totalUncuratedPhotos} ${submissions}`
                : `${votingEnds} ${this.fmt(this.contest().votingEndDate)}`;
        }
        return chooseWinners;
    }
}
